/**
 * quizzes.js — Quiz public shell.
 *
 * Owns: window.Quiz, QuizApi, boot initializeQuiz, and the lazy import of
 * the AI generator.
 * Does not own: quiz-taking UI (quiz-screen.js) or AI generation
 * (ai-quiz-generator.js).
 *
 * quiz-screen.js is imported statically so the engine is present at boot.
 * ai-quiz-generator.js is imported on first #ai-quiz navigation (or when
 * notes/PDF ask for a quiz) so students who never open AI Quiz do not
 * download it.
 */

import {
    initializeQuiz,
    loadQuiz,
    startFlashcardMode,
    showCustomQuizCreator,
    showQuizCategories,
    showQuizProgress,
    selectAnswer,
    nextQuestion,
    previousQuestion,
    finishQuiz,
    cancelActiveQuiz,
    retakeQuiz,
    saveCustomQuiz,
} from './quiz-screen.js?v=1';

let _aiQuizMod = null;

async function ensureAIQuiz() {
    if (!_aiQuizMod) {
        try {
            _aiQuizMod = await import('./ai-quiz-generator.js?v=9');
        } catch (err) {
            _aiQuizMod = null;
            throw err;
        }
    }
    return _aiQuizMod;
}

async function showAIQuizInterface() {
    const mod = await ensureAIQuiz();
    return mod.showAIQuizInterface();
}

async function generateAIQuiz() {
    const mod = await ensureAIQuiz();
    return mod.generateAIQuiz();
}

async function generateFromTopic(opts) {
    const mod = await ensureAIQuiz();
    return mod.generateFromTopic(opts);
}

async function setNextQuestionCount(n) {
    const mod = await ensureAIQuiz();
    return mod.setNextQuestionCount(n);
}

async function loadSampleContent() {
    const mod = await ensureAIQuiz();
    return mod.loadSampleContent();
}

async function quizFromNote(noteTitle, noteHtml) {
    const mod = await ensureAIQuiz();
    return mod.quizFromNote(noteTitle, noteHtml);
}

async function quizFromPDF(pageText, pdfTitle, pageNum) {
    const mod = await ensureAIQuiz();
    return mod.quizFromPDF(pageText, pdfTitle, pageNum);
}

// Drain the AI pending queue when the network returns, without loading the
// generator at boot. If nothing is queued the module stays on the network.
window.addEventListener('online', async () => {
    const pending = (typeof Storage !== 'undefined' && Storage.loadPendingQuizRequestsV2)
        ? Storage.loadPendingQuizRequestsV2()
        : [];
    if (!Array.isArray(pending) || !pending.length) return;
    try {
        const mod = await ensureAIQuiz();
        mod.processPendingRequests();
    } catch (err) {
        console.error('[Quiz] pending AI requests could not drain:', err);
    }
});

window.Quiz = {
    initializeQuiz,
    loadQuiz,
    startFlashcardMode,
    showCustomQuizCreator,
    showQuizCategories,
    showQuizProgress,
    selectAnswer,
    nextQuestion,
    previousQuestion,
    finishQuiz,
    cancelActiveQuiz,
    retakeQuiz,
    showAIQuizInterface,
    generateAIQuiz,
    setNextQuestionCount,
    generateFromTopic,
    loadSampleContent,
    quizFromNote,
    quizFromPDF
};

// ── ES module surface (roadmap §3 & Track C) ──────────────────────────────────
// window.Quiz above is an explicit assignment and survives conversion.
// Pruned obsolete internal globals now that event delegation handles all quiz controls.
// Only verified cross-module bindings are retained.
// From: node scripts/required-globals.js quizzes.js --names
const _quizzesGlobals = {
    // showQuizCategories is app.js's navigation entry point for this screen.
    // app.js:543 called `loadQuizCategories()` — a name that exists nowhere in this
    // repo — so every visit to Quizzes threw and the category grid never rendered.
    // The function was renamed at some point and the call site was never updated;
    // Track C's shim prune could not have restored it, because there was no such
    // export to keep. Found in the console on 2026-08-10, not by any test.
    showQuizCategories,
    generateAIQuiz, loadQuiz, saveCustomQuiz, startFlashcardMode
};
export const QuizApi = _quizzesGlobals;
if (typeof window !== 'undefined') Object.assign(window, _quizzesGlobals);
